export type NotificationTone = 'success' | 'error' | 'warning' | 'info';

export interface AppNotification {
  id: string;
  title?: string;
  message: string;
  tone: NotificationTone;
  duration: number;
  createdAt: number;
}

interface NotifyInput {
  title?: string;
  message: string;
  tone?: NotificationTone;
  duration?: number;
}

type NotificationListener = (notifications: AppNotification[]) => void;

const DEFAULT_DURATION_MS = 8000;
const listeners = new Set<NotificationListener>();
const notifications: AppNotification[] = [];
const removalTimers = new Map<string, number>();

let nativeAlert: typeof window.alert | null = null;
let nativeConfirm: typeof window.confirm | null = null;
let overridesInstalled = false;

const emit = () => {
  const snapshot = [...notifications];
  listeners.forEach((listener) => listener(snapshot));
};

const generateNotificationId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `notification-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const normalizeText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (value instanceof Error) {
    return value.message.trim();
  }

  return String(value ?? '').trim();
};

const inferTone = (message: string): NotificationTone => {
  const normalized = message.toLowerCase();

  if (/(failed|error|denied|invalid|unable|cannot|missing|not authenticated|blocked)/.test(normalized)) {
    return 'error';
  }

  if (/(success|successfully|completed|approved|generated|saved|copied|created|restored|connected)/.test(normalized)) {
    return 'success';
  }

  if (/(warning|please|required|select|disconnect|overdue|reconnecting|review|missing)/.test(normalized)) {
    return 'warning';
  }

  return 'info';
};

const capitalizeFirst = (value: string): string => {
  if (!value) {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const toDeclarativeConfirmMessage = (rawMessage: string): string => {
  const normalized = rawMessage.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return 'Action will proceed.';
  }

  const withoutQuestionMark = normalized.replace(/\?+/g, '').trim();
  const plainPrompt = withoutQuestionMark
    .replace(/^are you sure(?: that)?\s*/i, '')
    .replace(/^do you want to\s*/i, '')
    .replace(/^would you like to\s*/i, '')
    .replace(/^confirm\s*/i, '')
    .trim();

  const deleteMatch = plainPrompt.match(/^delete\s+(.+)$/i);
  if (deleteMatch) {
    return `${capitalizeFirst(deleteMatch[1].trim())} will be deleted.`;
  }

  const removeMatch = plainPrompt.match(/^remove\s+(.+)$/i);
  if (removeMatch) {
    return `${capitalizeFirst(removeMatch[1].trim())} will be removed.`;
  }

  const archiveMatch = plainPrompt.match(/^archive\s+(.+)$/i);
  if (archiveMatch) {
    return `${capitalizeFirst(archiveMatch[1].trim())} will be archived.`;
  }

  const sendMatch = plainPrompt.match(/^send\s+(.+)$/i);
  if (sendMatch) {
    return `${capitalizeFirst(sendMatch[1].trim())} will be sent.`;
  }

  return `${capitalizeFirst(plainPrompt || withoutQuestionMark)}. Action will proceed.`;
};

const normalizeNotificationText = (value: unknown): string => {
  const normalized = normalizeText(value);
  return normalized.includes('?')
    ? toDeclarativeConfirmMessage(normalized)
    : normalized;
};

export const dismissNotification = (id: string): void => {
  const index = notifications.findIndex((notification) => notification.id === id);
  if (index === -1) {
    return;
  }

  notifications.splice(index, 1);

  const timerId = removalTimers.get(id);
  if (timerId !== undefined) {
    window.clearTimeout(timerId);
    removalTimers.delete(id);
  }

  emit();
};

export const notify = ({ title, message, tone, duration }: NotifyInput): string => {
  const normalizedMessage = normalizeNotificationText(message);
  if (!normalizedMessage) {
    return '';
  }

  const id = generateNotificationId();
  const notification: AppNotification = {
    id,
    title: normalizeNotificationText(title) || undefined,
    message: normalizedMessage,
    tone: tone ?? inferTone(normalizedMessage),
    duration: duration ?? DEFAULT_DURATION_MS,
    createdAt: Date.now(),
  };

  notifications.push(notification);
  emit();

  const timerId = window.setTimeout(() => {
    dismissNotification(id);
  }, notification.duration);

  removalTimers.set(id, timerId);
  return id;
};

export const notifySuccess = (message: string, title?: string): string => notify({ message, title, tone: 'success' });
export const notifyError = (message: string, title?: string): string => notify({ message, title, tone: 'error' });
export const notifyWarning = (message: string, title?: string): string => notify({ message, title, tone: 'warning' });
export const notifyInfo = (message: string, title?: string): string => notify({ message, title, tone: 'info' });
export const legacyAlert = (message?: unknown): void => {
  const normalizedMessage = normalizeText(message);
  if (!normalizedMessage) {
    return;
  }

  notify({
    message: normalizedMessage,
    tone: inferTone(normalizedMessage),
  });
};

export const legacyConfirm = (message?: unknown): boolean => {
  const normalizedMessage = normalizeText(message);
  notify({
    title: 'Action proceeding',
    message: toDeclarativeConfirmMessage(normalizedMessage),
    tone: 'warning',
  });
  return true;
};

export const subscribeToNotifications = (listener: NotificationListener): (() => void) => {
  listeners.add(listener);
  listener([...notifications]);

  return () => {
    listeners.delete(listener);
  };
};

export const installNotificationOverrides = (): void => {
  if (overridesInstalled || typeof window === 'undefined') {
    return;
  }

  nativeAlert = window.alert.bind(window);
  nativeConfirm = window.confirm.bind(window);

  window.alert = ((message?: unknown) => {
    legacyAlert(message);
  }) as typeof window.alert;

  window.confirm = ((message?: string) => {
    return legacyConfirm(message);
  }) as typeof window.confirm;

  overridesInstalled = true;
};

export const restoreNativeNotifications = (): void => {
  if (!overridesInstalled || typeof window === 'undefined') {
    return;
  }

  if (nativeAlert) {
    window.alert = nativeAlert;
  }

  if (nativeConfirm) {
    window.confirm = nativeConfirm;
  }

  overridesInstalled = false;
};
